import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import request from 'lib/request';
import type {
  AddressGroup,
  AddressGroupFilters,
  AddressRunSummary,
  AddressScope,
  AddressVariation,
  BulkType,
  ProcessStatus,
  Run,
  StartProcessResponse,
} from '../types';
import { startProcessRequest } from '../shared';
import {
  isTerminal,
  processRef,
  useProcessStatus,
} from '../use-process-status';
import { filterQuery } from './utils';

type SetError = (error: string | null) => void;

/**
 * The error a response carries. request.get resolves a failure to an { error }
 * payload rather than rejecting, so a load must check for it; otherwise a
 * 404/500 renders as an empty happy-path table with no sign anything failed.
 * @param {unknown} json The response.
 * @return {string | null} Its error, or null.
 */
export function errorFrom(json: unknown): string | null {
  return json && typeof json === 'object' && 'error' in json
    ? String(json.error)
    : null;
}

const MERGE_RUN_NOUN: Record<BulkType, string> = {
  entities: 'deduplication',
  addresses: 'combine',
};

/**
 * The run's bulk type. When the hub passes it on navigation it is used directly
 * and the probe skipped (which also doubled the run-wide queries). Otherwise it
 * is read off a one-row subclusters page: top-level when the response carries
 * it (so an empty run still answers), else the row's own. A failed probe
 * surfaces an error with Retry rather than guessing "entities"; guessing
 * crashed the entity page on an address run (address rows have no `members`).
 * @param {number} runId The clustering run.
 * @param {BulkType | null} initial The type the hub passed, if any.
 * @return {object} The type, a load error, and a retry.
 */
export function useRunBulkType(
  runId: number,
  initial: BulkType | null = null
): { bulkType: BulkType | null; error: string | null; retry: () => void } {
  const [bulkType, setBulkType] = useState<BulkType | null>(initial);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (initial) {
      setBulkType(initial);
      return undefined;
    }
    let stale = false;
    setBulkType(null);
    setError(null);
    void (async () => {
      const json = (await request.get(
        `/admin/bulk_dedupe/subclusters?clusteringProcessId=${runId}&limit=1`
      )) as
        | { bulkType?: BulkType; subclusters?: { bulkType?: BulkType }[] }
        | undefined;
      if (stale) return;
      const err = errorFrom(json);
      if (err) {
        setError(err);
        return;
      }
      setBulkType(
        (json?.bulkType ?? json?.subclusters?.[0]?.bulkType) === 'addresses'
          ? 'addresses'
          : 'entities'
      );
    })();
    return () => {
      stale = true;
    };
  }, [runId, initial, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { bulkType, error, retry };
}

/**
 * One page of an address run's groups, re-fetched on every query change. A
 * response superseded by a newer request is dropped, so it can neither
 * overwrite newer rows nor clear the spinner early. The row/count setters are
 * exposed so a review toggle can patch the server's answer in place without a
 * full reload.
 * @param {number} runId The clustering run.
 * @param {object} query The page, filters, search and refresh token.
 * @param {SetError} setError Reports a failed load.
 * @return {object} The page's groups, counts, summary and scope.
 */
export function useAddressGroups(
  runId: number,
  query: {
    page: number;
    pageSize: number;
    filters: AddressGroupFilters;
    search: string;
    // Bumped to re-fetch the current page after a combine, reversal or toggle.
    refreshToken: number;
  },
  setError: SetError
) {
  const [groups, setGroups] = useState<AddressGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [summary, setSummary] = useState<AddressRunSummary | null>(null);
  const [scope, setScope] = useState<AddressScope | null>(null);
  const [loading, setLoading] = useState(true);
  const { page, pageSize, filters, search, refreshToken } = query;

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    const params = [
      `clusteringProcessId=${runId}`,
      `limit=${pageSize}`,
      `offset=${(page - 1) * pageSize}`,
      filterQuery(filters),
      search ? `q=${encodeURIComponent(search)}` : '',
    ].filter(Boolean);
    void (async () => {
      const json = (await request.get(
        `/admin/bulk_dedupe/subclusters?${params.join('&')}`
      )) as {
        subclusters?: AddressGroup[];
        total?: number;
        counts?: Record<string, number>;
        summary?: AddressRunSummary;
        scope?: AddressScope;
      };
      if (ignore) return;
      const err = errorFrom(json);
      if (err) {
        setError(err);
        setLoading(false);
        return;
      }
      setGroups(json?.subclusters ?? []);
      setTotal(json?.total ?? 0);
      setCounts(json?.counts ?? {});
      setSummary(json?.summary ?? null);
      setScope(json?.scope ?? null);
      setLoading(false);
    })();
    return () => {
      ignore = true;
    };
  }, [runId, page, pageSize, filters, search, refreshToken, setError]);

  return {
    groups,
    setGroups,
    total,
    counts,
    setCounts,
    summary,
    scope,
    loading,
  };
}

/**
 * The run's merge/combine executions, loaded on mount.
 * @param {number} runId The clustering run.
 * @param {SetError} setError Reports a failed load.
 * @return {object} The executions and a reload.
 */
export function useRunExecutions(runId: number, setError: SetError) {
  const [executions, setExecutions] = useState<Run[]>([]);

  // Returns the fetched runs so a caller can read them without waiting for the
  // `executions` state it also sets (e.g. the post-reversal refused count).
  const loadExecutions = useCallback(async (): Promise<Run[]> => {
    const json = (await request.get(
      `/admin/bulk_dedupe/runs?clusteringProcessId=${runId}`
    )) as { runs?: Run[] };
    const err = errorFrom(json);
    if (err) {
      setError(err);
      return [];
    }
    const runs = json?.runs ?? [];
    setExecutions(runs);
    return runs;
  }, [runId, setError]);

  useEffect(() => {
    void loadExecutions();
  }, [loadExecutions]);

  return { executions, loadExecutions };
}

/**
 * The run's failed deduplications, loaded on demand. Generic over the row type:
 * an entity run's failures are Subclusters, an address run's AddressFailures.
 * @param {number} runId The clustering run.
 * @param {SetError} setError Reports a failed load.
 * @return {object} The failures, their total, and a load.
 */
export function useRunFailures<TRow>(runId: number, setError: SetError) {
  const [failures, setFailures] = useState<TRow[]>([]);
  const [failuresTotal, setFailuresTotal] = useState(0);

  const loadFailures = useCallback(async () => {
    const json = (await request.get(
      `/admin/bulk_dedupe/failures?clusteringProcessId=${runId}`
    )) as { failures?: TRow[]; total?: number };
    const err = errorFrom(json);
    if (err) {
      setError(err);
      return;
    }
    setFailures(json?.failures ?? []);
    setFailuresTotal(json?.total ?? 0);
  }, [runId, setError]);

  return { failures, failuresTotal, loadFailures, setFailures };
}

/**
 * Watches the clustering run itself. onGenerated fires only when generation
 * finished while watched: opening an already-processed run gets a terminal
 * first poll, and reloading then just duplicates the mount load.
 * @param {number} runId The clustering run.
 * @param {Function} onGenerated Called once generation finishes.
 * @param {SetError} setError Reports a lost connection.
 * @return {object} The run's status and whether it is generating or failed.
 */
export function useClusteringStatus(
  runId: number,
  onGenerated: () => void,
  setError: SetError
) {
  const clusterProc = useMemo(() => processRef(runId), [runId]);
  const wasClusteringRef = useRef(false);
  const clusterStatus = useProcessStatus(
    clusterProc,
    (status: ProcessStatus) => {
      if (status.overall_status === 'processed' && wasClusteringRef.current)
        onGenerated();
    },
    useCallback(
      () => setError('Lost connection to the clustering run.'),
      [setError]
    )
  );
  const clustering = !!clusterStatus && !isTerminal(clusterStatus);
  // Generation reached a terminal state with a failure: no groups will finish,
  // so the run can only be abandoned. `clustering` is already false here (the
  // step is terminal), which is why the page must lock off this flag instead.
  const generationFailed =
    !!clusterStatus &&
    isTerminal(clusterStatus) &&
    clusterStatus.overall_status === 'failed';
  useEffect(() => {
    if (clustering) wasClusteringRef.current = true;
  }, [clustering]);

  return { clusterStatus, clustering, generationFailed };
}

/**
 * The run's merge (execute) and reversal processes. On mount it resumes one
 * still draining, so navigating away and back keeps the progress bar and the
 * Execute/Reverse guards.
 * @param {object} options The run, its type, and the completion callbacks.
 * @return {object} The processes, their statuses, and execute and reverse.
 */
export function useRunProcesses({
  runId,
  bulkType,
  setError,
  onMergeDone,
  onReverseDone,
}: {
  runId: number;
  bulkType: BulkType;
  setError: SetError;
  onMergeDone: () => void;
  onReverseDone: () => void;
}) {
  const [mergeProc, setMergeProc] = useState<StartProcessResponse | null>(null);
  const [reverseProc, setReverseProc] = useState<StartProcessResponse | null>(
    null
  );
  // True until the active-process probe resolves: Execute must stay disabled
  // until then, or opening a page while a merge drains fires a second execute
  // before the running one is known.
  const [activeLoading, setActiveLoading] = useState(true);
  // An execute POST in flight, so a double-click can't send a second one.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setActiveLoading(true);
    void (async () => {
      try {
        const json = (await request.get(
          `/admin/bulk_dedupe/active_process?clusteringProcessId=${runId}`
        )) as { active?: (StartProcessResponse & { kind: string }) | null };
        if (cancelled) return;
        // request.get resolves a failure to an { error } payload. A failed probe
        // left unread reads as "nothing running" — Execute re-enables mid-merge
        // and a click gets a 422; surface it instead.
        const err = errorFrom(json);
        if (err) {
          setError(err);
          return;
        }
        const active = json?.active;
        if (!active) return;
        if (active.kind === 'reverse') setReverseProc(active);
        else setMergeProc(active);
      } finally {
        if (!cancelled) setActiveLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, setError]);

  const mergeStatus = useProcessStatus(
    mergeProc,
    onMergeDone,
    useCallback(
      () => setError(`Lost connection to the ${MERGE_RUN_NOUN[bulkType]} run.`),
      [bulkType, setError]
    )
  );
  const reverseStatus = useProcessStatus(
    reverseProc,
    onReverseDone,
    useCallback(
      () => setError('Lost connection to the reversal run.'),
      [setError]
    )
  );

  const start = useCallback(
    async (url: string, body: Record<string, unknown>) => {
      setError(null);
      const res = await startProcessRequest(url, body);
      if (!res.data) setError(res.error ?? 'Request failed');
      return res.data ?? null;
    },
    [setError]
  );

  const execute = useCallback(async () => {
    setSubmitting(true);
    try {
      const proc = await start('/admin/bulk_dedupe/execute', {
        clusteringProcessId: runId,
      });
      if (proc) setMergeProc(proc);
    } finally {
      setSubmitting(false);
    }
  }, [start, runId]);

  const reverse = useCallback(
    async (url: string, body: Record<string, unknown>) => {
      const proc = await start(url, body);
      if (!proc) return;
      setReverseProc(proc);
    },
    [start]
  );

  const mergeRunning = !!mergeProc && !isTerminal(mergeStatus);
  const reversalRunning = !!reverseProc && !isTerminal(reverseStatus);

  // Execute is held while the active-process probe is still loading or an
  // execute POST is in flight, so neither a double-click nor an early open can
  // fire a competing merge.
  const executeBusy = activeLoading || submitting;

  return {
    mergeProc,
    mergeStatus,
    reverseProc,
    reverseStatus,
    mergeRunning,
    reversalRunning,
    executeBusy,
    execute,
    reverse,
  };
}

/**
 * The three pending reversal selections (a whole execution, a group, or a
 * single address) and the confirm handlers that fire each one. A confirm first
 * clears any prior refused tally, then starts the reversal and drops the
 * selection, which closes its modal.
 * @param {Function} reverse Starts a reversal.
 * @param {Function} resetRefused Clears the refused tally.
 * @return {object} The selections, their setters, and the confirms.
 */
export function useReversalSelection(
  reverse: (url: string, body: Record<string, unknown>) => Promise<void>,
  resetRefused: () => void
) {
  const [groupToReverse, setGroupToReverse] = useState<AddressGroup | null>(
    null
  );
  const [runToReverse, setRunToReverse] = useState<Run | null>(null);
  const [variationToReverse, setVariationToReverse] =
    useState<AddressVariation | null>(null);

  /** Reverses the selected execution. */
  const confirmRunReverse = () => {
    if (!runToReverse) return;
    resetRefused();
    void reverse('/admin/bulk_dedupe/reverse', {
      mergeProcessId: runToReverse.id,
    });
    setRunToReverse(null);
  };

  /** Reverses the selected group. */
  const confirmReverse = () => {
    if (!groupToReverse) return;
    resetRefused();
    void reverse('/admin/bulk_dedupe/reverse_subcluster', {
      subclusterId: groupToReverse.id,
    });
    setGroupToReverse(null);
  };

  /**
   * Reverses the selected variation. A refused variation reversal rides the
   * run summary's refused tally like any other reversal, so it raises the
   * "could not be reversed" banner (and shows its reason under the group's
   * status). A variation that merely leaves its group partially reversed is a
   * success and refuses nothing.
   */
  const confirmVariationReverse = () => {
    if (!variationToReverse) return;
    resetRefused();
    void reverse('/admin/bulk_dedupe/reverse_member', {
      memberIds: variationToReverse.memberIds,
    });
    setVariationToReverse(null);
  };

  return {
    groupToReverse,
    setGroupToReverse,
    runToReverse,
    setRunToReverse,
    variationToReverse,
    setVariationToReverse,
    confirmRunReverse,
    confirmReverse,
    confirmVariationReverse,
  };
}

/**
 * A review POST over a set of ids. The server's rows and counts are
 * authoritative; an id with a POST in flight is pending so a rapid
 * double-click can't fire competing updates (last-response-wins).
 * @param {string} url The review endpoint.
 * @param {Function} bodyFor The POST body for ids and a value.
 * @param {Function} onUpdated Called with the server's answer.
 * @param {SetError} setError Reports a refused update.
 * @return {object} The toggle and the ids in flight.
 */
function useReviewUpdate<TResponse>(
  url: string,
  bodyFor: (ids: number[], value: boolean) => Record<string, unknown>,
  onUpdated: (response: TResponse) => void,
  setError: SetError
) {
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  // Latest callbacks without changing toggle's identity, which memoized table
  // columns depend on.
  const onUpdatedRef = useRef(onUpdated);
  const bodyForRef = useRef(bodyFor);
  useLayoutEffect(() => {
    onUpdatedRef.current = onUpdated;
    bodyForRef.current = bodyFor;
  });

  const toggle = useCallback(
    async (ids: number[], value: boolean) => {
      setError(null);
      setPendingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
      try {
        const res = await request.post<TResponse, { error: string }>(
          url,
          bodyForRef.current(ids, value),
          new AbortController()
        );
        if (res.type === 'success') {
          onUpdatedRef.current(res.data);
        } else if (res.type === 'error') {
          setError(res.data?.error ?? 'Request failed');
        } else {
          setError(res.message);
        }
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [url, setError]
  );

  return { toggle, pendingIds };
}

/**
 * The include/exclude review wiring for an address run: a group-level toggle
 * and a variation-level toggle, both patching the server's authoritative rows
 * and counts into the already-loaded page (then reloading, as the page may no
 * longer match the filters).
 * @param {object} options The page's row and count setters, reload and error.
 * @return {object} Each toggle with its in-flight ids.
 */
export function useGroupReview({
  setGroups,
  setCounts,
  reloadPage,
  setError,
}: {
  setGroups: Dispatch<SetStateAction<AddressGroup[]>>;
  setCounts: Dispatch<SetStateAction<Record<string, number>>>;
  reloadPage: () => void;
  setError: SetError;
}) {
  const applyGroupUpdates = useCallback(
    (
      updated: AddressGroup[],
      freshCounts: Record<string, number>,
      skipped = 0
    ) => {
      const byId = new Map(updated.map((g) => [g.id, g]));
      setGroups((prev) => prev.map((g) => byId.get(g.id) ?? g));
      setCounts(freshCounts);
      // The server skips a group no longer under review (the run was reloaded
      // out from under this toggle): say why rather than leaving a stale row, as
      // the entity run does.
      if (skipped > 0)
        setError('This group is no longer under review; the run was reloaded.');
      // Re-read the page: it may no longer match the filters, and a load
      // already in flight predates this change.
      reloadPage();
    },
    [reloadPage, setGroups, setCounts, setError]
  );

  // Checking includes a group, unchecking excludes it; re-including a group
  // with a variation left out comes back partially excluded.
  const { toggle: toggleGroups, pendingIds } = useStatusToggle<AddressGroup>(
    applyGroupUpdates,
    setError
  );
  const { toggle: toggleVariation, pendingIds: pendingMemberIds } =
    useMemberInclusionToggle<AddressGroup>(
      useCallback(
        (updated, freshCounts) => applyGroupUpdates([updated], freshCounts),
        [applyGroupUpdates]
      ),
      setError
    );

  return { toggleGroups, pendingIds, toggleVariation, pendingMemberIds };
}

/**
 * Include/exclude for one or more groups of one run. Groups no longer under
 * review are skipped server-side; the answer holds only the changed ones.
 * @param {Function} onUpdated Called with the changed rows, counts and skips.
 * @param {SetError} setError Reports a refused update.
 * @return {object} The toggle and the ids in flight.
 */
export function useStatusToggle<TRow extends { id: number }>(
  onUpdated: (
    rows: TRow[],
    counts: Record<string, number>,
    skipped: number
  ) => void,
  setError: SetError
) {
  return useReviewUpdate<{
    subclusters: TRow[];
    skipped: number;
    counts: Record<string, number>;
  }>(
    '/admin/bulk_dedupe/subcluster_status',
    (subclusterIds, nextInScope) => ({
      subclusterIds,
      status: nextInScope ? 'in_scope' : 'excluded',
    }),
    (res) => onUpdated(res.subclusters, res.counts, res.skipped),
    setError
  );
}

/**
 * Include/exclude for one variation of an address group (all its rows),
 * answered with the whole group.
 * @param {Function} onUpdated Called with the group and counts.
 * @param {SetError} setError Reports a refused update.
 * @return {object} The toggle and the ids in flight.
 */
export function useMemberInclusionToggle<TRow extends { id: number }>(
  onUpdated: (row: TRow, counts: Record<string, number>) => void,
  setError: SetError
) {
  return useReviewUpdate<{ subcluster: TRow; counts: Record<string, number> }>(
    '/admin/bulk_dedupe/subcluster_member_inclusion',
    (memberIds, included) => ({ memberIds, included }),
    (res) => onUpdated(res.subcluster, res.counts),
    setError
  );
}
